<template>
   <AccountProfileField :title="fieldLabel" :fieldNotice="field.fieldNotice">
      <!-- Using slot instead of details string to render checkboxes -->
      <template #details>
         <RequestLoader data-cy="contact-preferences" :isLoading="isLoadingCheckboxes || isLoadingContactOptions">
            <form>
               <fieldset class="mb-6">
                  <legend class="sr-only">{{ $t("account.contact_preferences.label") }}</legend>
                  <div>
                     <CheckboxInput
                        class="rounded-md border border-grey-10"
                        v-for="option in contactOptions"
                        :id="option.id"
                        :key="option.id"
                        :value="selectedOptions.includes(option.id)"
                        :label="option.label"
                        :group="`contact-preferences-${option.id}`"
                        @update:value="onCheckboxClick(option.id, $event)"
                        :disabled="isLoadingCheckboxes"
                     />
                  </div>
               </fieldset>
            </form>
         </RequestLoader>
      </template>
   </AccountProfileField>
</template>

<script setup lang="ts">
import UsersApi from "@/data/api-layer/usersApi";
import ContactPreferenceApi, { type ContactMethodsResp } from "@/data/api-layer/contactPreferencesApi";
import { useUserStore } from "@/stores/userStore";
import type { AccountProfileField } from "@/components/account/accountTypes";
import { useToastKey } from "@/plugins/useToast";

// Declarations
// -----------------------------------------
const $t = useI18n().t;
const useToast = inject(useToastKey);
const userStore = useUserStore();

const props = defineProps<{
   field: AccountProfileField;
}>();

// State
const selectedOptions = ref<string[]>([]);
const isLoadingCheckboxes = ref(false);
const isLoadingContactOptions = ref(true);
const contactOptions = ref<ContactMethodsResp[]>([]);

// Computed
// -----------------------------------------
const storedPreferences = computed(() => {
   return userStore.user.preferredContactMethodIds || [];
});

// In a very rare case the contact options can't be fetched don't show the "Please select" label and replace it with an error message
const fieldLabel = computed(() => {
   if (isLoadingContactOptions.value || contactOptions.value.length) {
      return props.field.label;
   } else {
      return $t("validation.contact_preferences_fetch_error");
   }
});

// Methods
// -----------------------------------------
async function onCheckboxClick(id: string, isChecked: boolean) {
   isLoadingCheckboxes.value = true;

   // Create payload based on the current state of the checkboxes
   let payload = [...selectedOptions.value];
   if (isChecked) {
      if (!payload.includes(id)) {
         payload.push(id);
      }
   } else {
      payload = payload.filter((inputId) => inputId !== id);
   }

   try {
      await UsersApi.updateUserFields({ preferred_contact_method_ids: payload });
      // Only update the local state if the API call was successful
      selectedOptions.value = payload;
   } catch (error) {
      console.error("Error updating contact preferences", error);
      useToast({ text: $t("validation.error_generic_msg"), type: "error" });
   } finally {
      isLoadingCheckboxes.value = false;
   }
}

// Lifecycle
// -----------------------------------------
onMounted(async () => {
   try {
      // Get the list of available contact preference options
      contactOptions.value = await ContactPreferenceApi.getAll();

      // Set the selected options based on current user data
      selectedOptions.value = [...storedPreferences.value];
   } catch (error) {
      console.error("Error fetching contact preferences", error);
      useToast({ text: $t("validation.contact_preferences_fetch_error"), type: "error" });
   } finally {
      isLoadingContactOptions.value = false;
   }
});
</script>
